import React, { useMemo } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Label } from 'recharts';

interface GaugeChartProps {
  value: number;
  min: number;
  max: number;
  unit?: string;
  label?: string;
  width?: number;
  height?: number;
  threshold?: {
    warning?: number;
    critical?: number;
  };
}

export function GaugeChart({
  value,
  min,
  max,
  unit = '',
  label = '',
  width = 200,
  height = 200,
  threshold = { warning: 0.7, critical: 0.9 },
}: GaugeChartProps) {
  // Normalize value to be within 0-100 range
  const normalizedValue = useMemo(() => {
    const range = max - min;
    if (range <= 0) return 50; // Default to middle if range is invalid

    // Clamp value between min and max
    const clampedValue = Math.max(min, Math.min(max, value));

    // Convert to percentage (0-100)
    return ((clampedValue - min) / range) * 100;
  }, [value, min, max]);

  // Calculate colors based on thresholds
  const valueColor = useMemo(() => {
    if (normalizedValue / 100 >= (threshold.critical || 0.9)) return '#dc2626'; // Red
    if (normalizedValue / 100 >= (threshold.warning || 0.7)) return '#eab308'; // Yellow
    return '#16a34a'; // Green
  }, [normalizedValue, threshold]);

  // Prepare data for gauge chart
  const gaugeData = useMemo(() => {
    return [
      { name: 'value', value: normalizedValue },
      { name: 'empty', value: 100 - normalizedValue },
    ];
  }, [normalizedValue]);

  // Format the display value
  const displayValue = useMemo(() => {
    return value.toFixed(1);
  }, [value]);

  return (
    <div className="relative" style={{ width, height }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={gaugeData}
            cx="50%"
            cy="80%"
            startAngle={180}
            endAngle={0}
            innerRadius="60%"
            outerRadius="80%"
            paddingAngle={0}
            dataKey="value"
            blendStroke
            isAnimationActive={true}
          >
            <Cell key="value-cell" fill={valueColor} stroke={valueColor} />
            <Cell key="empty-cell" fill="#e5e7eb" stroke="#e5e7eb" />
            <Label position="center" dy={-20} className="text-lg font-semibold" value={displayValue} />
            {unit && <Label position="center" dy={5} className="text-sm text-muted-foreground" value={unit} />}
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      {/* Sensor name label positioned under the gauge */}
      {label && <div className="absolute bottom-0 left-0 right-0 text-center text-sm font-medium mt-2">{label}</div>}

      {/* Min/Max ticks */}
      <div className="absolute bottom-0 left-0 text-xs text-muted-foreground">{min.toFixed(0)}</div>
      <div className="absolute bottom-0 right-0 text-xs text-muted-foreground">{max.toFixed(0)}</div>
    </div>
  );
}
